<template>
  <section>
    <div class="guests-page__head" style="margin: 4px 0 12px">
      <h2 class="guests-page__title" style="font-size: 18px">
        Camere / Posti letto
      </h2>
      <button class="villa-btn villa-btn--primary" @click="openCreateRoom">
        <i class="fa-solid fa-plus"></i> Nuova camera
      </button>
    </div>

    <div v-if="loading" class="guests-loading">Caricamento…</div>

    <template v-else>
      <div v-if="rooms.length === 0" class="guests-loading">
        Nessuna camera in questa struttura. Crea la prima camera.
      </div>

      <div v-else class="rooms-grid">
        <div v-for="room in rooms" :key="room.id" class="room-panel">
          <div class="room-panel__head">
            <div>
              <h2 class="room-panel__title">{{ room.name }}</h2>
              <div class="room-panel__meta">
                <template v-if="room.floor">Piano {{ room.floor }}</template>
                <template v-if="room.floor && room.area"> · </template>
                <template v-if="room.area">{{ room.area }}</template>
                <span> · {{ (room.beds || []).length }} posti letto</span>
              </div>
            </div>
            <div class="room-panel__actions">
              <button
                class="villa-btn villa-btn--ghost"
                @click="openCreateBed(room)"
              >
                <i class="fa-solid fa-plus"></i> Posto letto
              </button>
              <button
                class="villa-btn villa-btn--ghost"
                @click="openEditRoom(room)"
              >
                <i class="fa-solid fa-pen-to-square"></i> Modifica
              </button>
              <button
                class="villa-btn villa-btn--danger"
                @click="askDeactivate('room', room)"
              >
                <i class="fa-solid fa-ban"></i> Disattiva
              </button>
            </div>
          </div>

          <div class="room-panel__body">
            <div v-if="(room.beds || []).length === 0" class="beds-empty">
              Nessun posto letto in questa camera.
            </div>

            <div v-for="bed in room.beds" :key="bed.id" class="bed-row">
              <div class="bed-row__left">
                <span class="bed-row__code">{{ bed.code }}</span>
                <span :class="['bed-badge', 'bed-badge--' + bed.status]">
                  {{ bedStatusLabel(bed.status) }}
                </span>
                <span
                  v-if="bed.status === 'occupied'"
                  class="bed-row__occupant"
                >
                  da {{ occupantName(bed) }}
                </span>
              </div>
              <div class="bed-row__actions">
                <template v-if="bed.status === 'occupied'">
                  <button
                    class="villa-btn villa-btn--danger"
                    @click="openRelease(bed)"
                  >
                    <i class="fa-solid fa-right-from-bracket"></i> Libera
                  </button>
                </template>
                <template v-else-if="bed.status === 'available'">
                  <button
                    class="villa-btn villa-btn--primary"
                    @click="openAssign(room, bed)"
                  >
                    <i class="fa-solid fa-user-plus"></i> Assegna
                  </button>
                  <select
                    class="search-box"
                    style="min-width: 140px; padding: 6px 8px"
                    :value="bed.status"
                    @change="changeBedStatus(bed, $event.target.value)"
                  >
                    <option value="available">Disponibile</option>
                    <option value="maintenance">Manutenzione</option>
                    <option value="unavailable">Non disponibile</option>
                  </select>
                  <button
                    class="icon-btn icon-btn--danger"
                    title="Disattiva posto letto"
                    @click="askDeactivate('bed', bed)"
                  >
                    <i class="fa-solid fa-trash-can"></i>
                  </button>
                </template>
                <template v-else>
                  <select
                    class="search-box"
                    style="min-width: 140px; padding: 6px 8px"
                    :value="bed.status"
                    @change="changeBedStatus(bed, $event.target.value)"
                  >
                    <option value="available">Disponibile</option>
                    <option value="maintenance">Manutenzione</option>
                    <option value="unavailable">Non disponibile</option>
                  </select>
                  <button
                    class="icon-btn icon-btn--danger"
                    title="Disattiva posto letto"
                    @click="askDeactivate('bed', bed)"
                  >
                    <i class="fa-solid fa-trash-can"></i>
                  </button>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Modale Camera (crea/modifica) -->
    <VueFinalModal v-model="roomModal.open" content-class="villa-modal">
      <h3 class="villa-modal__title">
        {{ roomModal.mode === "edit" ? "Modifica camera" : "Nuova camera" }}
      </h3>
      <div class="form-grid">
        <div class="form-field form-field--full">
          <label>Nome <span class="required">*</span></label>
          <input v-model.trim="roomModal.form.name" type="text" />
        </div>
        <div class="form-field">
          <label>Piano</label>
          <input v-model.trim="roomModal.form.floor" type="text" />
        </div>
        <div class="form-field">
          <label>Area / reparto</label>
          <input v-model.trim="roomModal.form.area" type="text" />
        </div>
        <div class="form-field form-field--full">
          <label>Struttura</label>
          <input type="text" :value="facilityName" disabled />
        </div>
        <div class="form-field form-field--full">
          <label>Note</label>
          <textarea v-model.trim="roomModal.form.notes" rows="2"></textarea>
        </div>
      </div>
      <div class="form-actions">
        <button
          class="villa-btn villa-btn--ghost"
          @click="roomModal.open = false"
        >
          Annulla
        </button>
        <button
          class="villa-btn villa-btn--primary"
          :disabled="roomModal.busy"
          @click="saveRoom"
        >
          {{ roomModal.busy ? "Salvataggio…" : "Salva" }}
        </button>
      </div>
    </VueFinalModal>

    <!-- Modale Posto letto (crea) -->
    <VueFinalModal v-model="bedModal.open" content-class="villa-modal">
      <h3 class="villa-modal__title">Nuovo posto letto</h3>
      <div class="form-grid">
        <div class="form-field">
          <label>Codice <span class="required">*</span></label>
          <input
            v-model.trim="bedModal.form.code"
            type="text"
            placeholder="es. A, 1, Letto 1"
          />
        </div>
        <div class="form-field">
          <label>Stato iniziale</label>
          <select v-model="bedModal.form.status">
            <option value="available">Disponibile</option>
            <option value="maintenance">Manutenzione</option>
            <option value="unavailable">Non disponibile</option>
          </select>
        </div>
        <div class="form-field form-field--full">
          <label>Note</label>
          <textarea v-model.trim="bedModal.form.notes" rows="2"></textarea>
        </div>
      </div>
      <div class="form-actions">
        <button
          class="villa-btn villa-btn--ghost"
          @click="bedModal.open = false"
        >
          Annulla
        </button>
        <button
          class="villa-btn villa-btn--primary"
          :disabled="bedModal.busy"
          @click="saveBed"
        >
          {{ bedModal.busy ? "Salvataggio…" : "Crea" }}
        </button>
      </div>
    </VueFinalModal>

    <!-- Conferma disattivazione -->
    <VueFinalModal v-model="confirm.open" content-class="villa-confirm">
      <h3 class="villa-confirm__title">
        Disattiva {{ confirm.kind === "room" ? "camera" : "posto letto" }}
      </h3>
      <p class="villa-confirm__text">
        Vuoi disattivare <strong>{{ confirm.label }}</strong
        >?
      </p>
      <div class="villa-confirm__actions">
        <button
          class="villa-btn villa-btn--ghost"
          :disabled="confirm.busy"
          @click="confirm.open = false"
        >
          Annulla
        </button>
        <button
          class="villa-btn villa-btn--danger"
          :disabled="confirm.busy"
          @click="confirmDeactivate"
        >
          {{ confirm.busy ? "Disattiva…" : "Disattiva" }}
        </button>
      </div>
    </VueFinalModal>

    <!-- Modale assegnazione ospite a letto -->
    <VueFinalModal v-model="assignModal.open" content-class="villa-modal">
      <h3 class="villa-modal__title">Assegna ospite</h3>
      <p class="villa-confirm__text">
        Camera <strong>{{ assignModal.roomName }}</strong> · posto letto
        <strong>{{ assignModal.bedCode }}</strong>
      </p>
      <div class="form-grid">
        <div class="form-field form-field--full">
          <label>Ospite della struttura <span class="required">*</span></label>
          <select v-model="assignModal.guestId">
            <option :value="null" disabled>— seleziona ospite —</option>
            <option v-for="g in assignModal.guests" :key="g.id" :value="g.id">
              {{ g.lastName }} {{ g.firstName }}
            </option>
          </select>
          <span
            v-if="assignModal.guests.length === 0"
            class="bed-row__occupant"
          >
            Nessun ospite attivo nella struttura. Creane uno dalla tab Ospiti.
          </span>
        </div>
        <div class="form-field">
          <label>Data inizio</label>
          <input v-model="assignModal.startDate" type="date" />
        </div>
        <div class="form-field form-field--full">
          <label>Note</label>
          <textarea v-model.trim="assignModal.notes" rows="2"></textarea>
        </div>
      </div>
      <div class="form-actions">
        <button
          class="villa-btn villa-btn--ghost"
          @click="assignModal.open = false"
        >
          Annulla
        </button>
        <button
          class="villa-btn villa-btn--primary"
          :disabled="assignModal.busy || !assignModal.guestId"
          @click="submitAssign"
        >
          {{ assignModal.busy ? "Assegna…" : "Assegna" }}
        </button>
      </div>
    </VueFinalModal>

    <!-- Conferma rilascio letto -->
    <VueFinalModal v-model="releaseConfirm.open" content-class="villa-confirm">
      <h3 class="villa-confirm__title">Libera posto letto</h3>
      <p class="villa-confirm__text">
        Liberare il posto letto <strong>{{ releaseConfirm.bedCode }}</strong
        >?
      </p>
      <div class="villa-confirm__actions">
        <button
          class="villa-btn villa-btn--ghost"
          :disabled="releaseConfirm.busy"
          @click="releaseConfirm.open = false"
        >
          Annulla
        </button>
        <button
          class="villa-btn villa-btn--danger"
          :disabled="releaseConfirm.busy"
          @click="submitRelease"
        >
          {{ releaseConfirm.busy ? "Libera…" : "Libera" }}
        </button>
      </div>
    </VueFinalModal>
  </section>
</template>

<script>
import { mapActions } from "vuex";

const emptyRoom = () => ({
  id: null,
  name: "",
  floor: "",
  area: "",
  notes: "",
});
const emptyBed = () => ({ code: "", status: "available", notes: "" });

const todayISO = () => {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
};

export default {
  name: "FacilityRoomsPanel",
  props: {
    facilityId: { type: [String, Number], required: true },
    facilityName: { type: String, default: "" },
  },
  emits: ["changed"],
  data() {
    return {
      rooms: [],
      loading: true,
      roomModal: {
        open: false,
        mode: "create",
        busy: false,
        form: emptyRoom(),
      },
      bedModal: { open: false, busy: false, roomId: null, form: emptyBed() },
      confirm: { open: false, kind: "room", id: null, label: "", busy: false },
      assignModal: {
        open: false,
        busy: false,
        bedId: null,
        bedCode: "",
        roomName: "",
        guestId: null,
        startDate: todayISO(),
        notes: "",
        guests: [],
      },
      releaseConfirm: { open: false, busy: false, bedId: null, bedCode: "" },
    };
  },
  methods: {
    ...mapActions([
      "fetchFacilityRooms",
      "createRoom",
      "updateRoom",
      "deactivateRoom",
      "createBed",
      "updateBed",
      "deactivateBed",
      "fetchFacilityGuests",
      "assignGuestToBed",
      "releaseAssignment",
    ]),
    bedStatusLabel(s) {
      return (
        {
          available: "Disponibile",
          occupied: "Occupato",
          maintenance: "Manutenzione",
          unavailable: "Non disponibile",
        }[s] || s
      );
    },
    // Nome dell'ospite occupante (D10A fix). Fallback controllato se il letto
    // risulta occupato ma manca il dato ospite.
    occupantName(bed) {
      const o = bed && bed.occupant;
      const full = o ? `${o.firstName || ""} ${o.lastName || ""}`.trim() : "";
      return full || "Ospite non disponibile";
    },
    async load() {
      this.loading = true;
      try {
        this.rooms = await this.fetchFacilityRooms(this.facilityId);
      } catch (err) {
        if (err?.response?.status !== 404) {
          this.$toast.error("Errore nel caricamento delle camere.");
        }
        this.rooms = [];
      } finally {
        this.loading = false;
      }
    },

    // --- Camera ---
    openCreateRoom() {
      this.roomModal = {
        open: true,
        mode: "create",
        busy: false,
        form: emptyRoom(),
      };
    },
    openEditRoom(room) {
      this.roomModal = {
        open: true,
        mode: "edit",
        busy: false,
        form: {
          id: room.id,
          name: room.name,
          floor: room.floor || "",
          area: room.area || "",
          notes: room.notes || "",
        },
      };
    },
    async saveRoom() {
      const f = this.roomModal.form;
      if (!f.name)
        return this.$toast.error("Il nome della camera è obbligatorio.");
      this.roomModal.busy = true;
      try {
        // facilityId sempre forzato al contesto struttura corrente.
        const payload = {
          name: f.name,
          floor: f.floor,
          area: f.area,
          notes: f.notes,
          facilityId: Number(this.facilityId),
        };
        if (this.roomModal.mode === "edit") {
          await this.updateRoom({ id: f.id, payload });
          this.$toast.success("Camera aggiornata.");
        } else {
          await this.createRoom(payload);
          this.$toast.success("Camera creata nella struttura.");
        }
        this.roomModal.open = false;
        await this.load();
        this.$emit("changed");
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.error ||
            err?.response?.data?.details ||
            "Errore salvataggio camera."
        );
      } finally {
        this.roomModal.busy = false;
      }
    },

    // --- Posto letto ---
    openCreateBed(room) {
      this.bedModal = {
        open: true,
        busy: false,
        roomId: room.id,
        form: emptyBed(),
      };
    },
    async saveBed() {
      const f = this.bedModal.form;
      if (!f.code)
        return this.$toast.error("Il codice del posto letto è obbligatorio.");
      this.bedModal.busy = true;
      try {
        await this.createBed({
          roomId: this.bedModal.roomId,
          code: f.code,
          status: f.status,
          notes: f.notes,
        });
        this.$toast.success("Posto letto creato.");
        this.bedModal.open = false;
        await this.load();
        this.$emit("changed");
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.error ||
            err?.response?.data?.details ||
            "Errore creazione posto letto."
        );
      } finally {
        this.bedModal.busy = false;
      }
    },
    async changeBedStatus(bed, newStatus) {
      if (newStatus === bed.status) return;
      try {
        await this.updateBed({ id: bed.id, payload: { status: newStatus } });
        this.$toast.success("Stato posto letto aggiornato.");
        await this.load();
        this.$emit("changed");
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.error || "Impossibile aggiornare lo stato."
        );
        await this.load();
      }
    },

    // --- Assegnazione / rilascio ospite (operatività letti, D6E) ---
    async openAssign(room, bed) {
      this.assignModal = {
        open: true,
        busy: false,
        bedId: bed.id,
        bedCode: bed.code,
        roomName: room.name,
        guestId: null,
        startDate: todayISO(),
        notes: "",
        guests: [],
      };
      // Solo ospiti attivi della struttura: niente assegnazioni cross-struttura.
      try {
        const all = await this.fetchFacilityGuests(this.facilityId);
        this.assignModal.guests = (Array.isArray(all) ? all : []).filter(
          (g) => g.status === "active"
        );
      } catch {
        this.assignModal.guests = [];
      }
    },
    async submitAssign() {
      if (!this.assignModal.guestId) return;
      this.assignModal.busy = true;
      try {
        await this.assignGuestToBed({
          guestId: this.assignModal.guestId,
          bedId: this.assignModal.bedId,
          startDate: this.assignModal.startDate || undefined,
          notes: this.assignModal.notes || undefined,
        });
        this.$toast.success("Ospite assegnato.");
        this.assignModal.open = false;
        await this.load();
        this.$emit("changed");
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.error ||
            err?.response?.data?.details ||
            "Assegnazione non riuscita."
        );
      } finally {
        this.assignModal.busy = false;
      }
    },
    openRelease(bed) {
      this.releaseConfirm = {
        open: true,
        busy: false,
        bedId: bed.id,
        bedCode: bed.code,
      };
    },
    async submitRelease() {
      this.releaseConfirm.busy = true;
      try {
        await this.releaseAssignment({ bedId: this.releaseConfirm.bedId });
        this.$toast.success("Posto letto liberato.");
        this.releaseConfirm.open = false;
        await this.load();
        this.$emit("changed");
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.error || "Rilascio non riuscito."
        );
      } finally {
        this.releaseConfirm.busy = false;
      }
    },

    // --- Disattivazione ---
    askDeactivate(kind, entity) {
      this.confirm = {
        open: true,
        kind,
        id: entity.id,
        label: kind === "room" ? entity.name : `posto letto ${entity.code}`,
        busy: false,
      };
    },
    async confirmDeactivate() {
      this.confirm.busy = true;
      try {
        if (this.confirm.kind === "room")
          await this.deactivateRoom(this.confirm.id);
        else await this.deactivateBed(this.confirm.id);
        this.$toast.success("Disattivazione completata.");
        this.confirm.open = false;
        await this.load();
        this.$emit("changed");
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.error ||
            err?.response?.data?.details ||
            "Disattivazione non consentita."
        );
      } finally {
        this.confirm.busy = false;
      }
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<style src="@/css/GuestsPage.css" />
<style src="@/css/RoomsPage.css" />
